import React, { useCallback, useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { HTTPResponse } from '@/class/HTTPResponse';
import { useAuthContext } from '../../context/AuthContext';
import { fetchData } from '../../lib/api';
import { adminCardAccent, adminMutedText, adminSectionTitle } from '../layout/adminStyles';

interface Visitor {
  date: string;
  count: number;
}

interface DailyPageView {
  access_date: string;
  view_count: number;
}

const formatChartDate = (accessDate: string): string => {
  const date = new Date(`${accessDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return accessDate;
  }

  return date.toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    timeZone: 'Asia/Tokyo',
  });
};

const VisitorChart: React.FC = () => {
  const { store } = useAuthContext();
  const [chartData, setChartData] = useState<Visitor[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadMonthlyPageViews = useCallback(async () => {
    if (!store?.id) {
      setChartData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const res: HTTPResponse = await fetchData(`store/${store.id}/daily-page-view/monthly`);
      const views = Array.isArray(res.data?.views) ? (res.data.views as DailyPageView[]) : [];

      setChartData(
        views.map((view) => ({
          date: formatChartDate(view.access_date),
          count: view.view_count,
        }))
      );
    } catch (err: unknown) {
      console.error(err);
      setChartData([]);
      setErrorMessage('PVデータの取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  }, [store?.id]);

  useEffect(() => {
    loadMonthlyPageViews();
  }, [loadMonthlyPageViews]);

  return (
    <div className={`${adminCardAccent} bg-gradient-to-br from-white to-teal-50/20`}>
      <h4 className={`${adminSectionTitle} mb-4 border-l-teal-400 pb-0`}>PV数の推移（過去31日）</h4>

      {isLoading ? (
        <p className={`${adminMutedText} text-sm`}>読み込み中...</p>
      ) : errorMessage ? (
        <p className={`${adminMutedText} text-sm`}>{errorMessage}</p>
      ) : chartData.length === 0 ? (
        <p className={`${adminMutedText} text-sm`}>表示できるPVデータがありません。</p>
      ) : (
        <div className="h-[220px] sm:h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
              <XAxis dataKey="date" tick={{ fill: '#78716c', fontSize: 12 }} />
              <YAxis
                allowDecimals={false}
                tick={{ fill: '#78716c', fontSize: 12 }}
                label={{ value: 'PV', angle: -90, position: 'insideLeft', fill: '#57534e', fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: '12px',
                  border: '1px solid #e7e5e4',
                  backgroundColor: '#fff',
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                name="PV数"
                stroke="#2dd4bf"
                strokeWidth={2}
                dot={{ fill: '#5eead4', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: '#14b8a6' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default VisitorChart;
